import { useEffect, useState } from "react";
import axios from "axios";
import { defaultHeaders } from "../../Redux/Constants/DefaultHeaders";
import Pen from "../../Assets/pen.svg";
import {
	Space,
	ConfigProvider,
	Form,
	InputNumber,
	Button,
	Popconfirm,
	notification,
	Input,
	Checkbox,
} from "antd";

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { logout } from "../../utils/index.js";
import CustomTable from "./CustomTable.js";
dayjs.extend(utc);
dayjs.extend(timezone);

export default function SmartSafeReconciliations() {
	const [notificationPopup, contextHolder] = notification.useNotification();
	const [form] = Form.useForm();
	const [sortedInfo, setSortedInfo] = useState([]);
	const [filteredInfo, setFilteredInfo] = useState({
		verified: [false],
	});
	const isEditing = (record) => record.key === editingKey;
	const [expandedTableEntries, setExpandedTableEntries] = useState([]);
	const [verified, setVerified] = useState(false);
	const [loading, setLoading] = useState(false);
	const [editingKey, setEditingKey] = useState("");
	const [tableEntries, setTableEntries] = useState([]);
	const [reloadTable, setReloadTable] = useState(false);
	const [expandedParentRowKeys, setExpandedParentRowKeys] = useState([]);
	const [bankingCoreOptions, setBankingCoreOptions] = useState([]);
	const [openVerifyPopconfirm, setOpenVerifyPopconfirm] = useState(false);
	const [showVerifyPopconfirm, setShowVerifyPopconfirm] = useState(true);

	useEffect(() => {
		const originData = [];
		const expandedOriginData = [];
		const fetchData = async () => {
			setLoading(true);
			let recordId = null;
			try {
				const requestData = await axios.request({
					method: "GET",
					headers: defaultHeaders(),
					url: `${process.env.REACT_APP_API_URL}api/admin/reconciliations/pouches`,
				});
				if (!requestData.data.data) {
					setLoading(false);
				} else {
					for (let data of requestData.data.data.requests) {
						recordId = data.pouch_id;
						originData.push({
							key: data.pouch_id,
							pouchId: data.pouch_id,
							bankingCore: data.banking_core,
							totalAmt: `$${data.total_amt.toFixed(2)}`,
							actualAmt: data.actual_amt
								? `$${data.actual_amt.toFixed(2)}`
								: null,
							creditGiven: data.credit_given === 1 ? true : false,
							safeName: data.safe_name,
							verified: data.verified === 1 ? true : false,
						});
						for (const requestRecord of data.request_records) {
							expandedOriginData.push({
								key: data.pouch_id,
								requestId: requestRecord.request_id,
								dateOfSubmission: requestRecord.date_of_submission,
								statedAmt: requestRecord.stated_amt,
								actualAmt: requestRecord.actual_amt,
							});
						}
					}
					setBankingCoreOptions(requestData.data.data.banking_core_options);
					setTableEntries(originData);
					setExpandedTableEntries(expandedOriginData);
					setLoading(false);
				}
			} catch (e) {
				if (e.response.data.status === 401) {
					logout();
				} else {
					notificationPopup.error({
						message: `Pouch id: ${recordId} encountered the following error`,
						description: e.message,
						placement: "top",
						showProgress: true,
						pauseOnHover: true,
					});
				}

				setLoading(false);
			}
		};
		fetchData();
	}, [reloadTable, notificationPopup]);

	const edit = (record) => {
		setVerified(record.verified);
		// Find the expanded row corresponding to this parent
		const expandedRows =
			expandedTableEntries.filter((e) => e.key === record.key) || {};
		const expandedFields = {};
		//Save each row in the form to be used later
		for (const row of expandedRows) {
			expandedFields[`${row.requestId}_actualAmt`] =
				row.actualAmt !== null
					? row.actualAmt.toString().replace("$", "")
					: row.statedAmt.replace("$", "");
		}
		form.setFieldsValue({
			...record,
			...expandedFields,
		});
		setEditingKey(record.key);
		setExpandedParentRowKeys([record.key]);
	};
	const cancel = () => {
		setEditingKey("");
	};

	const save = async (key) => {
		try {
			const row = await form.validateFields();
			setLoading(true);
			let res;
			// Update parent table
			const updatedParent = tableEntries.map((item) =>
				item.key === key ? { ...item, ...row } : item
			);
			// Extract all *_actualAmt fields and prefix with $
			const actualAmtRecords = Object.entries(row)
				.filter(([fieldName]) => fieldName.endsWith("_actualAmt"))
				.map(([fieldName, value]) => ({
					requestId: fieldName.replace("_actualAmt", ""),
					actualAmt: `$${Number(value).toFixed(2)}`,
				}));
			setTableEntries(updatedParent);
			const requestPayload = {
				records: actualAmtRecords,
				verified: row.verified,
			};
			// Update expanded table
			const updatedExpanded = expandedTableEntries.map((item) =>
				item.key === key
					? {
							...item,
							actualAmt: row[`${item.requestId}_actualAmt`],
					  }
					: item
			);
			setExpandedTableEntries(updatedExpanded);
			res = await axios.request({
				method: "PUT",
				headers: { ...defaultHeaders(), id: key },
				url: `${process.env.REACT_APP_API_URL}api/admin/reconciliations/pouches/${editingKey}`,
				data: requestPayload,
			});

			if (!res.data.error) {
				setReloadTable(!reloadTable);
				setEditingKey("");
			} else {
				notificationPopup.error({
					message: res.data.message,
					placement: "top",
					showProgress: true,
					pauseOnHover: true,
				});
			}
			setLoading(false);
		} catch (errInfo) {
			setLoading(false);
			if (errInfo.response.data.status === 401) {
				logout();
			}
			notificationPopup.error({
				message: "Validation Failed",
				description: JSON.stringify(errInfo?.response?.data?.data),
				placement: "top",
				showProgress: true,
				pauseOnHover: true,
			});
			console.log("Validate Failed:", errInfo);
		}
	};

	const handleVerifyOpenChange = (Opening) => {
		//Handle closing popup confirm logic clean up
		if (!Opening) {
			setOpenVerifyPopconfirm(Opening);
			return;
		}

		if (showVerifyPopconfirm) {
			setOpenVerifyPopconfirm(true);
		} else {
			save(editingKey);
		}
	};
	const tableColumns = [
		{
			title: "Pouch ID",
			dataIndex: "pouchId",
			key: "pouchId",
			sortOrder: sortedInfo.columnKey === "pouchId" ? sortedInfo.order : null,
			sorter: (a, b) => a.pouchId - b.pouchId,
			align: "center",
		},
		{
			title: "Safe Name",
			dataIndex: "safeName",
			key: "safeName",
			//sorter: (a, b) => a.safeName.localeCompare(b.safeName),
			align: "center",
			filters: tableEntries
				.map((item) => item.safeName)
				.filter((value, index, self) => self.indexOf(value) === index) // unique names
				.map((name) => ({ text: name, value: name })),

			filterSearch: true,
			filteredValue: filteredInfo.safeName || null,
			onFilter: (value, record) => record.safeName.startsWith(value),
		},
		{
			title: "Total Amount",
			dataIndex: "totalAmt",
			key: "totalAmt",
			align: "center",
		},
		{
			title: "Actual Amount",
			dataIndex: "actualAmt",
			key: "actualAmt",
			filters: [{ text: "Empty", value: null }],
			filteredValue: filteredInfo.actualAmt || null,
			onFilter: (value, record) => record.actualAmt === value,
			align: "center",
		},
		{
			title: "Banking Core",
			dataIndex: "bankingCore",
			key: "bankingCore",
			align: "center",
			render: (value) =>
				bankingCoreOptions.find((core) => core.id === value)?.name || value, // show name in table
		},
		{
			title: "Verified",
			dataIndex: "verified",
			key: "verified",
			editable: true,
			filters: [
				{ text: "True", value: true },
				{ text: "False", value: false },
			],
			filteredValue: filteredInfo.verified || null,
			onFilter: (value, record) => record.verified === value,
			align: "center",
		},
		{
			title: "Provisional Credit Given",
			dataIndex: "creditGiven",
			key: "creditGiven",
			filters: [
				{ text: "True", value: true },
				{ text: "False", value: false },
			],
			filteredValue: filteredInfo.creditGiven || null,
			onFilter: (value, record) => record.creditGiven === value,
			align: "center",
		},
		{
			title: "Action",
			dataIndex: "action",
			key: "action",
			render: (_, record) => {
				return isEditing(record) ? (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "10px",
						}}
					>
						<ConfigProvider
							theme={{
								token: {
									colorBgContainer: "#bcff00",
									colorBgElevated: "#333333",
								},
							}}
						>
							<Popconfirm
								title="Looks like the verify column hasn't been checked. Would you like to proceed if you've received the money?"
								onCancel={() => {}}
								cancelText="No"
								okText="Yes"
								onConfirm={() => save(record.key)}
								okButtonProps={{ type: "default" }}
								onOpenChange={handleVerifyOpenChange}
								open={openVerifyPopconfirm}
							>
								<Button
									style={{
										marginInlineEnd: 8,
									}}
								>
									Save
								</Button>
							</Popconfirm>

							<Popconfirm
								title="Are you sure?"
								onCancel={() => {}}
								cancelText="No"
								okText="Yes"
								onConfirm={() => cancel()}
								okButtonProps={{ type: "default" }}
							>
								<Button
									style={{
										marginInlineEnd: 8,
									}}
								>
									Cancel
								</Button>
							</Popconfirm>
						</ConfigProvider>
					</div>
				) : (
					<Space size="middle">
						<button
							style={{ backgroundColor: "transparent" }}
							onClick={() => edit(record)}
						>
							<img src={Pen} alt="Edit Button" />
						</button>
					</Space>
				);
			},
			align: "center",
		},
	];
	const expandedTableColumns = [
		{
			title: "Request Id",
			dataIndex: "requestId",
			align: "center",
		},
		{
			title: "Date Of Submission",
			dataIndex: "dateOfSubmission",
			align: "center",
		},
		{
			title: "Stated Amount",
			dataIndex: "statedAmt",
			align: "center",
		},
		{
			title: "Actual Amount",
			dataIndex: "actualAmt",
			align: "center",
			editable: true,
		},
	];

	/**
	 * @param {Object} props - Props passed to the editable cell.
	 * @param {boolean} props.editing - Whether this cell is currently being edited.
	 * @param {string} props.dataIndex - The column dataIndex of this cell.
	 * @param {string} props.title - The column title of this cell.
	 * @param {Object} props.record - The table row data for this cell.
	 * @param {React.ReactNode} props.children - The default content of the cell.
	 * @param {boolean} [props.isExpandedTable=false] - Whether this cell is inside an expanded table.
	 * @param {Object} [props.restProps] - Any additional props passed down to the cell.
	 * @returns {JSX.Element} The rendered editable cell.
	 */
	const EditableCell = ({
		editing,
		dataIndex,
		title,
		record,
		children,
		isExpandedTable = false,
		...restProps
	}) => {
		const booleanFields = ["creditGiven", "verified"];
		const numberFields = ["actualAmt"];
		let tdStyle = { textAlign: "center" };
		const getAmtColor = (value, expected) =>
			value !== expected ? "red" : "#bcff00";

		if (dataIndex === "actualAmt" && !isExpandedTable && editing) {
			return (
				<td {...restProps}>
					<Form.Item
						shouldUpdate={(prev, curr) =>
							Object.keys(curr).some((key) => key.endsWith("_actualAmt"))
						}
						noStyle
					>
						{({ getFieldValue }) => {
							const expandedRows = expandedTableEntries.filter(
								(e) => e.key === record.key
							);

							const total = expandedRows.reduce((sum, item) => {
								const fieldName = `${item.requestId}_actualAmt`;
								let val = getFieldValue(fieldName) ?? item.actualAmt ?? 0;

								if (typeof val === "string") val = Number(val.replace("$", ""));

								return sum + val;
							}, 0);

							const parentTotalAmt = Number(
								String(record.totalAmt).replace("$", "")
							);

							const isMismatch = Number(total.toFixed(2)) !== parentTotalAmt;

							return (
								<span style={{ color: isMismatch ? "red" : "#bcff00" }}>
									${total.toFixed(2)}
								</span>
							);
						}}
					</Form.Item>
				</td>
			);
		}
		if (!restProps.editable) {
			let value;
			if (booleanFields.includes(dataIndex)) {
				value = record[dataIndex] ? "True" : "False";
			} else if (dataIndex === "actualAmt") {
				tdStyle.color = getAmtColor(record[dataIndex], record.totalAmt);
				value = children;
			} else {
				value = children;
			}
			return (
				<td {...restProps} style={{ ...tdStyle, ...restProps.style }}>
					{value}
				</td>
			);
		}

		let displayValue = children;
		if (!editing) {
			if (booleanFields.includes(dataIndex)) {
				displayValue = record[dataIndex] ? "True" : "False";
			} else if (dataIndex === "actualAmt") {
				displayValue = record[dataIndex];
				tdStyle.color = getAmtColor(record[dataIndex], record.statedAmt);
			}
		}
		// Determine validation rules
		let rules = [];
		if (!isExpandedTable) {
			rules.push({ required: true, message: `Please input ${title}!` });
		}

		let inputNode;
		//Unique field names for expanded rows to avoid collision
		const fieldName = isExpandedTable
			? `${record.requestId}_${dataIndex}`
			: dataIndex;

		if (numberFields.includes(dataIndex)) {
			inputNode = <InputNumber controls={false} disabled={verified} />;
		} else if (booleanFields.includes(dataIndex)) {
			inputNode = (
				<Checkbox
					onChange={(e) => {
						setVerified(e.target.checked);
						setShowVerifyPopconfirm(!e.target.checked);
					}}
				/>
			);
		} else {
			inputNode = <Input.TextArea autoSize style={{ padding: "4px" }} />;
		}

		return (
			<td {...restProps} style={tdStyle}>
				{editing ? (
					<Form.Item
						key={`${record.key}-${dataIndex}`}
						name={fieldName}
						valuePropName={
							booleanFields.includes(dataIndex) ? "checked" : "value"
						}
						style={{ margin: 0 }}
						rules={rules}
						initialValue={record[dataIndex]}
					>
						{inputNode}
					</Form.Item>
				) : (
					displayValue
				)}
			</td>
		);
	};

	return (
		<CustomTable
			tableTitle="Smart Safe Reconciliations"
			form={form}
			setEditingKey={setEditingKey}
			reloadTable={reloadTable}
			setReloadTable={setReloadTable}
			tableEntries={tableEntries}
			tableColumns={tableColumns}
			loading={loading}
			expandedTableEntries={expandedTableEntries}
			expandedTableColumns={expandedTableColumns}
			expandedParentRowKeys={expandedParentRowKeys}
			setExpandedParentRowKeys={setExpandedParentRowKeys}
			expandedRowTableKey={(record) => record.requestId}
			cancel={cancel}
			contextHolder={contextHolder}
			setSortedInfo={setSortedInfo}
			setFilteredInfo={setFilteredInfo}
			EditableCell={EditableCell}
			isEditing={isEditing}
		/>
	);
}
